/**
 * What the ranking had to set aside to come back with anything.
 *
 * The server answers with a list of tokens — `filter:silhouette`,
 * `range:widened`, `range:dropped`, `availability:shown_as_orderable`,
 * `exhausted` — and the client used to collapse every one of them into a
 * single sentence about "widening the search". That sentence is true of the
 * budget and false of everything else, and it left the worst case unsaid: a
 * filter chip still lit while the deck behind it ignores her filter. The
 * screen was telling her she was looking at mermaid gowns while showing her
 * A-line ones.
 *
 * The vocabulary lives in `packages/domain/src/ranking.ts`. An unrecognised
 * token is kept rather than dropped, so a server that learns a new one still
 * says SOMETHING rather than going quiet — the generic sentence is exactly
 * right for a relaxation this client has not been taught to name.
 */
export type Relaxation =
  | { readonly kind: 'filters'; readonly attributeKeys: readonly string[] }
  | { readonly kind: 'rangeWidened' }
  | { readonly kind: 'rangeDropped' }
  | { readonly kind: 'orderable' }
  | { readonly kind: 'exhausted' }
  /** Nothing on the rail was in her size, so she is being shown all of it. */
  | { readonly kind: 'size' }
  | { readonly kind: 'other' }

export function parseRelaxations(tokens: readonly string[]): Relaxation[] {
  const filters: string[] = []
  let widened = false
  let dropped = false
  let orderable = false
  let exhausted = false
  let size = false
  let other = false

  for (const token of tokens) {
    if (token.startsWith('filter:')) {
      const key = token.slice('filter:'.length)
      if (key) filters.push(key)
      continue
    }
    if (token === 'range:widened') widened = true
    else if (token === 'range:dropped') dropped = true
    else if (token === 'availability:shown_as_orderable') orderable = true
    else if (token === 'exhausted') exhausted = true
    else if (token === 'size') size = true
    else other = true
  }

  const out: Relaxation[] = []
  if (filters.length > 0) out.push({ kind: 'filters', attributeKeys: filters })
  // Dropped supersedes widened: the ladder tries widening first, so a response
  // carrying both means widening was not enough, and saying "we widened it a
  // little" about a budget that is no longer applied at all is the same lie in
  // a smaller font.
  if (dropped) out.push({ kind: 'rangeDropped' })
  else if (widened) out.push({ kind: 'rangeWidened' })
  if (orderable) out.push({ kind: 'orderable' })
  if (size) out.push({ kind: 'size' })
  if (exhausted) out.push({ kind: 'exhausted' })
  if (other) out.push({ kind: 'other' })
  return out
}

/**
 * The attributes whose filters are lit on screen but not in force.
 *
 * The chips keep her selection — it is still her answer, and it comes back
 * into effect the moment the deck can honour it — so this is what the chip
 * needs in order to say "set aside" rather than quietly lying.
 */
export function setAsideFilterKeys(tokens: readonly string[]): string[] {
  return tokens.filter((t) => t.startsWith('filter:')).map((t) => t.slice('filter:'.length)).filter(Boolean)
}
